import type { CookieReader } from "../types/cookie-reader";

import { isValidVisitorId } from "./is-valid-visitor-id";
import { parseSegmentAnonymousId } from "./parse-segment-anonymous-id";
import {
  SEGMENT_ANONYMOUS_ID_COOKIE_NAME,
  VISITOR_ID_COOKIE_NAME,
} from "./visitor-id-constants";

function readCookieValueFromDocument(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${escapedName}=([^;]*)`),
  );

  if (!match?.[1]) {
    return undefined;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Resolves the canonical visitor id from request cookies without generating a
 * new value. Prefers `hi_visitor_id`, then a normalized Segment anonymous id.
 */
function getVisitorIdFromCookies(cookies: CookieReader): string | undefined {
  const visitorId = cookies.get(VISITOR_ID_COOKIE_NAME)?.value.trim();

  if (isValidVisitorId(visitorId)) {
    return visitorId;
  }

  const segmentAnonymousId = parseSegmentAnonymousId(
    cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE_NAME)?.value,
  );

  if (isValidVisitorId(segmentAnonymousId)) {
    return segmentAnonymousId;
  }

  return undefined;
}

/**
 * Reads the canonical visitor id from browser cookies for Segment bootstrap.
 */
function getVisitorIdFromDocumentCookie(): string | undefined {
  return getVisitorIdFromCookies({
    get: (name) => {
      const value = readCookieValueFromDocument(name);
      return value === undefined ? undefined : { value };
    },
  });
}

export { getVisitorIdFromCookies, getVisitorIdFromDocumentCookie };
